/**
 * Locale-aware date / time / date-time formatters.
 *
 * Three entry points cover every legacy variant in the portal:
 *
 *   - `formatDate`     — date only ("Jan 10, 2025")
 *   - `formatTime`     — time of day ("1:42 PM"), optionally with seconds
 *   - `formatDateTime` — date + time, with style preset
 *
 * Style presets for `formatDateTime`:
 *
 *   - `'short'`     → "Jan 10, 1:42 PM"             (year omitted, useful
 *                                                    when the surrounding
 *                                                    row implies a recent
 *                                                    timeframe)
 *   - `'full'`      → "Jan 10, 2025, 1:42 PM"       (year included)
 *   - `'weekday'`   → "Wed, Jan 10, 1:42 PM"        (weekday + month + day)
 *   - `'adaptive'`  → today  → time only ("1:42 PM"),
 *                     this year → short date+time,
 *                     older    → full date+time
 *   - `'locale'`    → raw `toLocaleString()` output (locale + region default)
 *
 * Every formatter accepts the union `DateInput` (string | number | Date |
 * null | undefined) and returns the configured `fallback` when input is
 * nullish or an invalid date. Default fallback is the em-dash `'—'`.
 */

import { toDate, type DateInput } from './types';

export type DateTimeStyle = 'short' | 'full' | 'weekday' | 'adaptive' | 'locale';

export interface FormatOptions {
  /** Returned for nullish / empty / invalid input. Default `'—'`. */
  fallback?: string;
}

export interface FormatTimeOptions extends FormatOptions {
  /** Include seconds ("1:42:30 PM"). Default `false`. */
  seconds?: boolean;
}

export interface FormatDateTimeOptions extends FormatOptions {
  /** Include seconds in the time portion (date+time styles only). Default `false`. */
  seconds?: boolean;
  /**
   * Append a timezone label to the time portion (e.g. "PST", "GMT-8").
   * Off by default — most surfaces show local time implicitly. Opt in
   * when the timezone is ambiguous to the reader (e.g. infra-style
   * tables that display server timestamps). Ignored for the `'locale'`
   * style, which delegates entirely to `toLocaleString()`.
   */
  timeZoneName?: Intl.DateTimeFormatOptions['timeZoneName'];
}

/** Date only, e.g. "Jan 10, 2025". */
export function formatDate(value: DateInput, options: FormatOptions = {}): string {
  const { fallback = '—' } = options;
  const date = toDate(value);
  if (!date) return fallback;

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Time of day, e.g. "1:42 PM" or "1:42:30 PM" when `seconds: true`. */
export function formatTime(value: DateInput, options: FormatTimeOptions = {}): string {
  const { fallback = '—', seconds = false } = options;
  const date = toDate(value);
  if (!date) return fallback;

  const formatOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };
  if (seconds) formatOptions.second = '2-digit';

  return date.toLocaleTimeString([], formatOptions);
}

/** Combined date + time, with style preset. */
export function formatDateTime(
  value: DateInput,
  style: DateTimeStyle = 'short',
  options: FormatDateTimeOptions = {},
): string {
  const { fallback = '—', seconds = false, timeZoneName } = options;
  const date = toDate(value);
  if (!date) return fallback;

  const secondsSlot: Pick<Intl.DateTimeFormatOptions, 'second'> = seconds
    ? { second: '2-digit' }
    : {};
  const zoneSlot: Pick<Intl.DateTimeFormatOptions, 'timeZoneName'> = timeZoneName
    ? { timeZoneName }
    : {};

  switch (style) {
    case 'short':
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        ...secondsSlot,
        ...zoneSlot,
      });

    case 'full':
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        ...secondsSlot,
        ...zoneSlot,
      });

    case 'weekday':
      return date.toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        ...secondsSlot,
        ...zoneSlot,
      });

    case 'adaptive':
      return formatAdaptive(date, seconds, timeZoneName);

    case 'locale':
      return date.toLocaleString();

    default: {
      const _: never = style;
      void _;
      return date.toLocaleString();
    }
  }
}

/**
 * Adaptive date+time formatting:
 *   - same day as now → time only ("1:42 PM")
 *   - same calendar year → short date+time ("Jan 10, 1:42 PM")
 *   - older → full date+time ("Jan 10, 2024, 1:42 PM")
 *
 * Mirrors the legacy `getShortDateTime` behavior so existing tabular
 * surfaces (NotificationPanel, Observability tab, etc.) keep their
 * adaptive labels.
 */
function formatAdaptive(
  date: Date,
  seconds: boolean,
  timeZoneName?: Intl.DateTimeFormatOptions['timeZoneName'],
): string {
  const now = new Date();
  const isSameDay =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  const secondsSlot: Pick<Intl.DateTimeFormatOptions, 'second'> = seconds
    ? { second: '2-digit' }
    : {};
  const zoneSlot: Pick<Intl.DateTimeFormatOptions, 'timeZoneName'> = timeZoneName
    ? { timeZoneName }
    : {};

  if (isSameDay) {
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      ...secondsSlot,
      ...zoneSlot,
    });
  }

  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...secondsSlot,
      ...zoneSlot,
    });
  }

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...secondsSlot,
    ...zoneSlot,
  });
}

/**
 * Time-of-day formatter that echoes the raw input on Invalid Date.
 *
 * Used by chat-style transcripts where each message is tagged with its
 * arrival time. Distinct from `formatTime` because the fallback is the
 * raw `timestamp` string rather than a sentinel — `formatTime` would
 * need `{ fallback: timestamp }` at every call site otherwise.
 *
 * Returns `''` for nullish input.
 */
export function formatTimeOfDay(timestamp: string | null | undefined): string {
  if (timestamp == null) return '';
  try {
    const d = new Date(timestamp);
    if (Number.isNaN(d.getTime())) return timestamp;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return timestamp;
  }
}

/**
 * Short month+day+time formatter that defaults to `''` on Invalid Date.
 *
 * Used by chat headers where the surrounding row doesn't already have
 * date context. Distinct from `formatDateTime(value, 'short')` because
 * the legacy fallback was an empty string rather than the em-dash.
 */
export function formatChatTimestamp(timestamp: string | null | undefined): string {
  if (timestamp == null) return '';
  try {
    const d = new Date(timestamp);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}
